import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Making statsd talk directly to a browser",
  "description": "",
  "published": "2012-06-15T00:00:00.000Z",
  "redirect_from": ["/blog/making-statsd-talk-directly-to-a-browser/swizec/4649"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "https://github.com/etsy/statsd"
      }}>{`StatsD`}</a>{` is "`}<em parentName="p">{`A network daemon that runs on the `}<a parentName="em" {...{
          "href": "http://nodejs.org/"
        }}>{`Node.js`}</a>{` platform and listens for statistics, like counters and timers, sent over `}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/User_Datagram_Protocol"
        }}>{`UDP`}</a>{` and sends aggregates to one or more pluggable backend services"`}</em></p>
    <p>{`I've previously mentioned StatsD in `}<em parentName="p"><a parentName="em" {...{
          "href": "http://www.zemanta.com/fruitblog/your-startup-needs-a-dashboard/"
        }}>{`Your startup needs a dashboard`}</a></em>{` over at Zemanta's fruitblog.`}</p>
    <p>{`The idea behind StatsD is this:`}</p>
    <ol>
      <li parentName="ol">{`Stuff happens`}</li>
      <li parentName="ol">{`Send metrics of "stuff" to a central service (StatsD)`}</li>
      <li parentName="ol">{`StatsD acts as a buffer, forwards aggregated metrics every X seconds`}</li>
    </ol>
    <p>{`Your architecture now has a central service that collects all of your metrics, then pushes them to appropriate software, that doesn't need to handle too much traffic and is guaranteed data will come from a single source in a sanitized format.`}</p>
    <p><img parentName="p" {...{
        "src": "https://distilleryimage0.instagram.com/1d60c314b6de11e1a9f71231382044a1_7.jpg",
        "alt": "A software architect's dream",
        "title": "A software architect's dream"
      }}></img></p>
    <h2 {...{
      "id": "straight-from-the-browser"
    }}>{`Straight from the browser?`}</h2>
    <p>{`Collecting data into StatsD works wonderfully. It's fast, reliable, extremely robust and you can give it just about any data you can think of.`}</p>
    <p>{`Unless your client is a browser.`}</p>
    <p>{`See, StatsD only accepts UDP packets and browsers don't let you send UDP packets. There's a valid excuse for this - it doesn't matter if some packets are lost, as long as whatever you're measuring isn't slowed down by the measuring.`}</p>
    <p>{`To solve this I created a simple proxy in node.js. It accepts normal HTTP requests and pushes data onward to StatsD. The simplicity, I hope, ensures speed.`}</p>
    <p>{`The API is a simple tracking pixel:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<img src="http://<address>?t=<type>&v=<value>&b=<bucket>" />
`}</code></pre>
    <p>{`Where `}<em parentName="p">{`type`}</em>{` is one of `}<em parentName="p">{`c `}</em>{`(counter)`}<em parentName="p">{`, t `}</em>{`(timer)`}<em parentName="p">{`, g `}</em>{`(gauge)`}<em parentName="p">{`.`}</em>{` As per StatsD naming convention. And `}<em parentName="p">{`bucket`}</em>{` is simply the name of your metric.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/personal-dashboard/blob/master/collectors/js_proxy/server.js"
      }}>{`The source`}</a>{` is on github. Feel free to use it.`}</p>
    <h2 {...{
      "id": "straight-to-the-browser"
    }}>{`Straight to the browser`}</h2>
    <p>{`Ok, so now we can collect data from the browser ... but I want to send it directly to a browser as well. None of that `}<a parentName="p" {...{
        "href": "http://graphite.wikidot.com/"
      }}>{`Graphite`}</a>{` stuff - I want to use some other fancy graphs and visualisations. Just because.`}</p>
    <p>{`To solve this problem I implemented a socket.io backend for StatsD. It, also, can be found on github -> `}<a parentName="p" {...{
        "href": "https://github.com/etsy/statsd/pull/102"
      }}>{`https://github.com/etsy/statsd/pull/102`}</a></p>
    <p>{`Hope the pull request gets merged soon, or at all for that matter. I really think this is a useful addition to StatsD because it means you can use whatever client-side javascript to do visualisations. In near real-time and all that.`}</p>
    <p>{`The data is sent over in a simple format:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{perSecond: {bucket1: 0.2,
             bucket2: 0.1
             },
 counts: {bucket1: 2,
          bucket2: 1
         },
 timers: {timer1: {upper: 2.4,
                   lower: 1.2,
                   count: 10}
         },
 gauges: {gauge1: 10
          },
 statsd: {numStats: 6},
 timestamp: <unix timestamp="">}
</unix>
`}</code></pre>
    <h2 {...{
      "id": "the-goal"
    }}>{`The goal?`}</h2>
    <p>{`If all goes well, I will soon be able to use cubism.js to draw a pretty timeseries of the traffic on this blog. And hey, who knows what else I can think of to add to a personal dashboard of my life ... I now have the basic framework. Time to start using it.`}</p>
    <p><a parentName="p" {...{
        "href": "http://square.github.com/cubism/"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/06/cubismjs-shiny-timeseries.png",
          "alt": "Cubism.js makes shiny timeseries",
          "title": "Cubism.js makes shiny timeseries"
        }}></img></a></p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.zemanta.com/fruitblog/your-startup-needs-a-dashboard/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/92004102_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.zemanta.com/fruitblog/your-startup-needs-a-dashboard/"
        }}>{`Your startup needs a dashboard`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.slideshare.net/quipo/scalable-architectures-taming-the-twitter-firehose"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/89469802_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.slideshare.net/quipo/scalable-architectures-taming-the-twitter-firehose"
        }}>{`Scalable Architectures - Taming the Twitter Firehose`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://oubiwann.blogspot.com/2011/06/txstatsd-preview.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_18_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://oubiwann.blogspot.com/2011/06/txstatsd-preview.html"
        }}>{`txStatsD Preview`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.artillerygames.com/2012/06/websocket-performance.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_03_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.artillerygames.com/2012/06/websocket-performance.html"
        }}>{`Experiments with WebSocket Performance (for HTML5 games)`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://obfuscurity.com/2012/05/A-Foolishly-Sensible-Proposal-for-Graphite"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_17_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://obfuscurity.com/2012/05/A-Foolishly-Sensible-Proposal-for-Graphite"
        }}>{`A Foolishly Sensible Proposal for Graphite`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://37signals.com/svn/posts/3185-link-our-implementation-of-statsd"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/91655784_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://37signals.com/svn/posts/3185-link-our-implementation-of-statsd"
        }}>{`LINK: Our implementation of Statsd`}</a></li>
    </ul>
    <hr></hr>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=4eaa9d08-7860-4eca-9d2d-eb7b8dad4f21",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      